/**
 * Grid settings panel — only relevant when `chart_type === 'grid'`.
 *
 * Exposes the backend's grid resolver features:
 *   - `group_by`: choose a single group field from the current DSID
 *   - `aggregates`: list of `{column, op}` rows for grand-total + per-group rollups
 *   - `sort_list`: ordered multi-column sort
 *
 * All fields are scoped to the currently-selected X DSID so we stay on a
 * single queryset root (the resolver enforces this too).
 */
import { ArrowDown, ArrowUp, Plus, Trash2 } from 'lucide-react';
import { Badge } from '../../../components/ui/badge';
import { Button } from '../../../components/ui/button';
import { Label } from '../../../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import {
  type BuilderFieldDef,
  type BuilderGridAggOp,
  type DsidCatalogResponse,
  type IndicatorBuilderConfig,
} from '../../../lib/api/reporting';

interface GridSettingsPanelProps {
  config: IndicatorBuilderConfig;
  onChange: (next: IndicatorBuilderConfig) => void;
  catalog: DsidCatalogResponse | null;
}

const AGG_OPS: { value: BuilderGridAggOp; label: string }[] = [
  { value: 'sum',            label: 'Sum' },
  { value: 'avg',            label: 'Average' },
  { value: 'min',            label: 'Min' },
  { value: 'max',            label: 'Max' },
  { value: 'count',          label: 'Count' },
  { value: 'distinct_count', label: 'Distinct count' },
];

/** Collect the axis picks (x, y, x2) as a deduped column list. Used as the
 *  default when `config.columns` isn't set yet — keeps the UI in sync with
 *  the resolver's new "respect picks" behaviour. */
function pickedAxesAsColumns(config: IndicatorBuilderConfig): Array<{ dsid: number; field_id: number }> {
  const picks: Array<{ dsid: number; field_id: number }> = [];
  const seen = new Set<string>();
  for (const axis of [config.x, config.y, config.x2]) {
    if (!axis || axis.dsid === undefined || axis.field_id === undefined) continue;
    const key = `${axis.dsid}-${axis.field_id}`;
    if (seen.has(key)) continue;
    seen.add(key);
    picks.push({ dsid: axis.dsid, field_id: axis.field_id });
  }
  return picks;
}

export function GridSettingsPanel({ config, onChange, catalog }: GridSettingsPanelProps) {
  const dsid = config.x?.dsid ?? null;
  const datasetFields = catalog?.datasets.find((d) => d.dsid === dsid)?.fields ?? [];
  const groupBy = config.group_by ?? null;
  const aggregates = config.aggregates ?? [];
  const sortList = config.sort_list ?? [];

  const effectiveColumns = config.columns && config.columns.length > 0
    ? config.columns
    : pickedAxesAsColumns(config);
  const effectiveKeys = new Set(effectiveColumns.map((c) => `${c.dsid}-${c.field_id}`));
  const availableToAdd: BuilderFieldDef[] = datasetFields.filter(
    (f) => !effectiveKeys.has(`${f.dsid}-${f.field_id}`),
  );

  const commitColumns = (next: Array<{ dsid: number; field_id: number }>) => {
    onChange({ ...config, columns: next });
  };

  const addColumn = (value: string) => {
    const fieldId = parseInt(value, 10);
    if (!Number.isFinite(fieldId) || dsid === null) return;
    commitColumns([...effectiveColumns, { dsid, field_id: fieldId }]);
  };

  const removeColumn = (idx: number) => {
    commitColumns(effectiveColumns.filter((_, i) => i !== idx));
  };

  const moveColumn = (idx: number, delta: -1 | 1) => {
    const next = [...effectiveColumns];
    const target = idx + delta;
    if (target < 0 || target >= next.length) return;
    [next[idx], next[target]] = [next[target], next[idx]];
    commitColumns(next);
  };

  const labelFor = (col: { dsid: number; field_id: number }): string => {
    const ds = catalog?.datasets.find((d) => d.dsid === col.dsid);
    const fd = ds?.fields.find((f) => f.field_id === col.field_id);
    return fd?.label ?? `DSID ${col.dsid} · F${col.field_id}`;
  };

  const setGroupBy = (value: string) => {
    if (value === 'none') {
      onChange({ ...config, group_by: null });
      return;
    }
    const fieldId = parseInt(value, 10);
    const fd = datasetFields.find((f) => f.field_id === fieldId);
    if (!fd || dsid === null) return;
    onChange({ ...config, group_by: { dsid, field_id: fieldId } });
  };

  const addAggregate = () => {
    const firstField = datasetFields[0];
    if (!firstField) return;
    const next = [...aggregates, { column: firstField.label, op: 'sum' as BuilderGridAggOp }];
    onChange({ ...config, aggregates: next });
  };

  const updateAggregate = (idx: number, patch: Partial<{ column: string; op: BuilderGridAggOp }>) => {
    const next = aggregates.map((a, i) => (i === idx ? { ...a, ...patch } : a));
    onChange({ ...config, aggregates: next });
  };

  const removeAggregate = (idx: number) => {
    const next = aggregates.filter((_, i) => i !== idx);
    onChange({ ...config, aggregates: next });
  };

  const addSort = () => {
    const firstField = datasetFields[0];
    if (!firstField) return;
    const next = [...sortList, { column: firstField.label, dir: 'asc' as const }];
    onChange({ ...config, sort_list: next });
  };

  const updateSort = (idx: number, patch: Partial<{ column: string; dir: 'asc' | 'desc' }>) => {
    const next = sortList.map((s, i) => (i === idx ? { ...s, ...patch } : s));
    onChange({ ...config, sort_list: next });
  };

  const removeSort = (idx: number) => {
    const next = sortList.filter((_, i) => i !== idx);
    onChange({ ...config, sort_list: next });
  };

  if (dsid === null) {
    return (
      <div className="text-xs text-muted-foreground py-3">
        Pick an X axis first — grid grouping applies to the chosen dataset.
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Columns (order + visibility) */}
      <div className="space-y-1">
        <div className="flex items-center justify-between">
          <Label className="text-xs">Columns</Label>
          {availableToAdd.length > 0 && (
            <Select value="" onValueChange={addColumn}>
              <SelectTrigger className="h-7 w-[140px] text-xs">
                <Plus className="h-3.5 w-3.5 mr-1" />
                <span>Add column</span>
              </SelectTrigger>
              <SelectContent>
                {availableToAdd.map((f) => (
                  <SelectItem key={f.field_id} value={String(f.field_id)}>
                    {f.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>
        {effectiveColumns.length === 0 ? (
          <div className="text-xs text-muted-foreground italic">
            No columns selected. Pick an X axis field (or use "Add column" above) to populate the grid.
          </div>
        ) : (
          <div className="space-y-1">
            {effectiveColumns.map((col, idx) => (
              <div key={`${col.dsid}-${col.field_id}`} className="flex items-center gap-2 px-2 py-1.5 rounded border border-border bg-card">
                <div className="min-w-0 flex-1">
                  <div className="text-xs font-medium truncate">{labelFor(col)}</div>
                  <Badge variant="outline" className="text-[10px] mt-0.5">
                    DSID {col.dsid} · F{col.field_id}
                  </Badge>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={() => moveColumn(idx, -1)}
                  disabled={idx === 0}
                  className="h-7 w-7 text-muted-foreground hover:text-foreground"
                  aria-label="Move column up"
                >
                  <ArrowUp className="h-3.5 w-3.5" />
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={() => moveColumn(idx, 1)}
                  disabled={idx === effectiveColumns.length - 1}
                  className="h-7 w-7 text-muted-foreground hover:text-foreground"
                  aria-label="Move column down"
                >
                  <ArrowDown className="h-3.5 w-3.5" />
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={() => removeColumn(idx)}
                  className="h-7 w-7 text-muted-foreground hover:text-destructive"
                  aria-label="Remove column"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Group by */}
      <div className="space-y-1">
        <Label className="text-xs">Group by</Label>
        <Select
          value={groupBy ? String(groupBy.field_id) : 'none'}
          onValueChange={setGroupBy}
        >
          <SelectTrigger className="h-8 text-xs">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="none">No grouping (flat table)</SelectItem>
            {datasetFields.map((f) => (
              <SelectItem key={f.field_id} value={String(f.field_id)}>
                {f.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Aggregates */}
      <div className="space-y-1">
        <div className="flex items-center justify-between">
          <Label className="text-xs">Aggregates (grand total / group rollup)</Label>
          <Button type="button" variant="ghost" size="sm" onClick={addAggregate} className="h-7">
            <Plus className="h-3.5 w-3.5 mr-1" />
            Add
          </Button>
        </div>
        {aggregates.length === 0 ? (
          <div className="text-xs text-muted-foreground italic">No aggregates configured.</div>
        ) : (
          <div className="space-y-1">
            {aggregates.map((a, idx) => (
              <div key={idx} className="grid grid-cols-[1fr_110px_auto] gap-2 items-center">
                <Select
                  value={a.column}
                  onValueChange={(v) => updateAggregate(idx, { column: v })}
                >
                  <SelectTrigger className="h-8 text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {datasetFields.map((f) => (
                      <SelectItem key={f.field_id} value={f.label}>{f.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select
                  value={a.op}
                  onValueChange={(v) => updateAggregate(idx, { op: v as BuilderGridAggOp })}
                >
                  <SelectTrigger className="h-8 text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {AGG_OPS.map((o) => (
                      <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={() => removeAggregate(idx)}
                  className="h-8 w-8 text-muted-foreground hover:text-destructive"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Sort list */}
      <div className="space-y-1">
        <div className="flex items-center justify-between">
          <Label className="text-xs">Multi-column sort</Label>
          <Button type="button" variant="ghost" size="sm" onClick={addSort} className="h-7">
            <Plus className="h-3.5 w-3.5 mr-1" />
            Add
          </Button>
        </div>
        {sortList.length === 0 ? (
          <div className="text-xs text-muted-foreground italic">Default sort by the first column.</div>
        ) : (
          <div className="space-y-1">
            {sortList.map((s, idx) => (
              <div key={idx} className="grid grid-cols-[1fr_110px_auto] gap-2 items-center">
                <Select
                  value={s.column}
                  onValueChange={(v) => updateSort(idx, { column: v })}
                >
                  <SelectTrigger className="h-8 text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {datasetFields.map((f) => (
                      <SelectItem key={f.field_id} value={f.label}>{f.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select
                  value={s.dir}
                  onValueChange={(v) => updateSort(idx, { dir: v as 'asc' | 'desc' })}
                >
                  <SelectTrigger className="h-8 text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="asc">Ascending</SelectItem>
                    <SelectItem value="desc">Descending</SelectItem>
                  </SelectContent>
                </Select>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={() => removeSort(idx)}
                  className="h-8 w-8 text-muted-foreground hover:text-destructive"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
